import { useTranslation } from "react-i18next";
import { useReportExportDisabled } from "../../../contexts/ReportLoadingContext";
import { useEffect, useRef, useState } from 'react';
import ChipPressableComponent from '../ComponentChipPressable/Index'
import { ReactComponent as DownloadIcon } from '../../../assets/svg/icons/download.svg'
import './styles.css'

type DownloadChipOption = 'PDF' | 'EXCEL' | 'REPORT';

interface DownloadChipProps {
  disabled?: boolean
  label?: string
  onSelect?: (type: DownloadChipOption) => void
  onPdfSelect?: () => void
  onExcelSelect?: () => void
  onReportSelect?: () => void
  enablePdf?: boolean
  enableExcel?: boolean
  enableReport?: boolean
}

export default function DownloadChip({
  label = '',
  disabled = false,
  onSelect,
  onPdfSelect,
  onExcelSelect,
  onReportSelect,
  enablePdf = false,
  enableExcel = false,
  enableReport = false,
  ...rest
}: DownloadChipProps) {
  const { t } = useTranslation();
  const reportDisabled = useReportExportDisabled();
  const isDisabled = disabled || reportDisabled;
  const [open, setOpen] = useState(false)
  const [theme, setTheme] = useState<'menu-light' | 'menu-dark'>('menu-light')
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const enabledOptions: DownloadChipOption[] = [
    ...(enableReport ? ['REPORT'] as DownloadChipOption[] : []),
    ...(enablePdf ? ['PDF'] as DownloadChipOption[] : []),
    ...(enableExcel ? ['EXCEL'] as DownloadChipOption[] : []),
  ]

  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      if (!wrapperRef.current) return
      if (!wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
        setTheme('menu-light')
      }
    }
    document.addEventListener('click', handleDocumentClick)
    return () => document.removeEventListener('click', handleDocumentClick)
  }, [])

  const handleMouseEnter = () => {
    if (!open) setTheme('menu-dark')
  }

  const handleMouseLeave = () => {
    if (!open) setTheme('menu-light')
  }

  const handleChipClick = (e: any) => {
    if (isDisabled) return
    e.stopPropagation()
    if (enabledOptions.length === 1) {
      handleSelect(enabledOptions[0])
      return
    }

    setOpen(prev => !prev)
    setTheme(prev => (!open ? 'menu-dark' : 'menu-light'))
  }

  const handleSelect = (type: DownloadChipOption) => {
    if (isDisabled) return
    if (type === 'PDF') {
      if (!enablePdf) return
      onPdfSelect?.()
    } else if (type === 'EXCEL') {
      if (!enableExcel) return
      onExcelSelect?.()
    } else {
      if (!enableReport) return
      onReportSelect?.()
    }
    onSelect?.(type)
    setOpen(false)
    setTheme('menu-light')
  }

  return (
    <div className={`download-chip__wrapper${isDisabled ? " download-chip--disabled" : ""}`} aria-disabled={isDisabled} aria-label={t("reportLayout.download")} ref={wrapperRef} {...rest}>
      <ChipPressableComponent
        disabled={isDisabled}
        aria-label={t("reportLayout.download")}
        theme={theme}
        label={label}
        IConComponent={
          <span className={`download-chip__icon ${theme === 'menu-light' ? 'download-chip__icon--light' : 'download-chip__icon--dark'}`}>
            <DownloadIcon />
          </span>
        }
        onClick={handleChipClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />

      {open && !isDisabled && enabledOptions.length > 1 && (
        <div className="download-chip__dropdown" onClick={e => e.stopPropagation()}>
          {enableReport && (
            <button type="button" className="download-chip__option" onClick={() => handleSelect('REPORT')}>
              {t("reportLayout.report")}
            </button>
          )}

          {enablePdf && (
            <button type="button" className="download-chip__option" onClick={() => handleSelect('PDF')}>
              PDF
            </button>
          )}
          {enableExcel && (
            <button type="button" className="download-chip__option" onClick={() => handleSelect('EXCEL')}>
              Excel
            </button>
          )}
        </div>
      )}
    </div>
  )
}
